/**
 * Which of a definition's lint findings are the POLICY check: the ceilings
 * dispatch applies (backend `definition_dispatch._ceiling`), recorded with
 * the ceiling's own reason as the message. The detail shows them as-is
 * under "Policy check" and the rest under "Definition lint"; the list
 * badges only the definition's own.
 *
 * Two kinds:
 * - The project's state (the harness not assigned, a pause, the registry's
 *   harness inactive): dispatch status, not the definition's fault, so the backend
 *   reports it as a WARNING to every lint caller (CI's `sanctum:lint`
 *   included). `isProjectStateFinding`.
 * - The definition's own breach (its agent not assigned, its model not
 *   allowed): an ERROR.
 *
 * The texts are the backend's: `project_settings.HARNESS_NOT_ASSIGNED` /
 * `AGENT_RUNS_PAUSED`, `definition_dispatch.HARNESS_UNAVAILABLE`, and
 * `_definition_ceiling`'s three reasons. Backend
 * `test_panel_policy_texts.py` pins this file to them, so the two never
 * drift apart. A definition's `model` is at most 128 characters
 * (`MODEL_MAX_LEN`), so the model reason is never cut at dispatch's
 * 200-character limit and keeps its "'s model set" ending.
 */

import type { LintFinding } from '../types/projectPanel.types';

const PROJECT_STATE_TEXTS: readonly string[] = [
  'the Sanctum agent harness is not assigned to this project (ask a Sanctum admin)',
  'agent runs are paused for this project',
  'the sanctum-agent feature is not active in the feature registry',
];

const CEILING_TEXTS: readonly string[] = [
  'no agent (pi or claude-code) is assigned to this project',
];

const CEILING_SHAPES: readonly RegExp[] = [
  /^agent \S+ is not assigned to this project$/,
  /^model .+ is not in \S+'s model set$/,
];

/** The project's state, at whatever severity: shown by the setup status and pause, not on each entry. */
export function isProjectStateFinding(finding: LintFinding): boolean {
  return PROJECT_STATE_TEXTS.includes(finding.message);
}

export function isCeilingFinding(finding: LintFinding): boolean {
  if (isProjectStateFinding(finding)) return true;
  if (finding.severity !== 'error') return false;
  const { message } = finding;
  return CEILING_TEXTS.includes(message) || CEILING_SHAPES.some((shape) => shape.test(message));
}

export interface SplitFindings {
  /** The definition's own findings. */
  lint: LintFinding[];
  /** Ceiling breaches, shown as-is. */
  policy: LintFinding[];
}

export function splitFindings(findings: LintFinding[]): SplitFindings {
  return {
    lint: findings.filter((f) => !isCeilingFinding(f)),
    policy: findings.filter(isCeilingFinding),
  };
}
